import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { MessageSquare, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

interface AgentCardProps {
  agent: {
    type: string;
    name: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    available: boolean;
    capabilities: string[];
    useCases: string[];
  };
  isSelected: boolean;
  onSelect: () => void;
  onStartConversation: () => void;
}

const colorClasses = {
  blue: 'from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-300 dark:border-blue-700',
  green: 'from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30 border-green-300 dark:border-green-700',
  purple: 'from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30 border-purple-300 dark:border-purple-700',
  pink: 'from-pink-100 to-pink-200 dark:from-pink-900/30 dark:to-pink-800/30 border-pink-300 dark:border-pink-700',
  orange: 'from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-800/30 border-orange-300 dark:border-orange-700',
};

const iconColorClasses = {
  blue: 'text-blue-600 dark:text-blue-400',
  green: 'text-green-600 dark:text-green-400',
  purple: 'text-purple-600 dark:text-purple-400',
  pink: 'text-pink-600 dark:text-pink-400',
  orange: 'text-orange-600 dark:text-orange-400',
};

export function AgentCard({ agent, isSelected, onSelect, onStartConversation }: AgentCardProps) {
  const Icon = agent.icon;
  const colorClass = colorClasses[agent.color as keyof typeof colorClasses];
  const iconColorClass = iconColorClasses[agent.color as keyof typeof iconColorClasses];

  return (
    <Card 
      className={cn(
        'p-6 transition-all relative',
        agent.available ? 'cursor-pointer hover:shadow-lg' : 'opacity-60 cursor-not-allowed',
        isSelected && agent.available && `bg-gradient-to-br ${colorClass}`
      )}
      onClick={agent.available ? onSelect : undefined}
    >
      {/* Coming Soon Badge */}
      {!agent.available && (
        <div className="absolute top-4 right-4">
          <Badge className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700">
            Coming Soon
          </Badge>
        </div>
      )}

      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={cn(
            'w-12 h-12 rounded-lg flex items-center justify-center',
            isSelected && agent.available ? 'bg-white dark:bg-gray-800' : 'bg-gray-100 dark:bg-gray-700'
          )}>
            <Icon className={cn(
              'w-6 h-6', 
              agent.available ? iconColorClass : 'text-gray-400 dark:text-gray-500'
            )} />
          </div>
          <div>
            <h3 className={cn(
              'font-semibold',
              agent.available ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
            )}>
              {agent.name}
            </h3>
            <Badge variant="secondary" className="mt-1">
              {agent.type}
            </Badge>
          </div>
        </div>
      </div>

      <p className={cn(
        'text-sm mb-4',
        agent.available ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'
      )}>
        {agent.description}
      </p>

      {/* Capabilities */}
      <div className="mb-4">
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">CAPABILITIES</h4>
        <div className="flex flex-wrap gap-1">
          {agent.capabilities.slice(0, 3).map((capability, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {capability}
            </Badge>
          ))}
          {agent.capabilities.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{agent.capabilities.length - 3} more
            </Badge>
          )}
        </div>
      </div>

      {/* Example Use Cases */}
      <div className="mb-6">
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">EXAMPLE QUESTIONS</h4>
        <div className="space-y-1">
          {agent.useCases.slice(0, 2).map((useCase, index) => (
            <p key={index} className="text-xs text-gray-500 dark:text-gray-400 italic">
              "{useCase}"
            </p>
          ))}
        </div>
      </div>

      {/* Action Button */}
      <Button
        onClick={(e) => {
          e.stopPropagation();
          if (agent.available) {
            onStartConversation();
          }
        }}
        disabled={!agent.available}
        className={cn(
          'w-full',
          agent.available 
            ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
        )}
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        {agent.available ? 'Start Conversation' : 'Coming Soon'}
        {agent.available && <ArrowRight className="w-4 h-4 ml-2" />}
      </Button>
    </Card>
  );
}